import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { ImagesService } from '@services/image-service';
import Icon from '@components/atoms/icon/icon';
import Slider from '@components/atoms/slider/slider';
import { Picture } from '@interfaces/models/picture';
import useHideGlobalScrollbar from '@hooks/use-hide-global-scrollbar';
import ProductImage from '@components/atoms/vc-images/components/product-image';
import 'swiper/css';
import useWindowSize from '@hooks/use-window-size';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useFeatureFlag from '@hooks/use-feature-flags';
import styles from './product-gallery.module.scss';

const PhotoSwipe = dynamic(() => import('./photo-swiper/photo-swiper'), {
  ssr: false,
});

export interface GalleryPicture {
  src: string;
  msrc?: string; // Used during zoom animation
  w: number;
  h: number;
}

interface GalleryProps {
  pictures?: Picture[];
  contextCssClass?: string;
  productPath: string;
  defaultAlt: string;
}

const ProductGallery: React.FC<GalleryProps> = (props) => {
  const { pictures, contextCssClass, productPath, defaultAlt } = props;
  const [image, setImage] = useState<Picture>(pictures?.[0]);
  const [prevButtonDisabled, setPrevButtonDisabled] = useState<boolean>(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
  const [imageViewEventList, setImageViewEventList] = useState<number[]>([]);
  const [showImageSwiper, setShowImageSwiper] = useState<boolean>(false);
  const [swiperImages, setSwiperImages] = useState<Array<GalleryPicture>>([]);
  const [activePicIndex, setActivePicIndex] = useState<number>(0);
  const { isWiderThanMd } = useWindowSize();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const { isSemPDP } = useFeatureFlag();
  const { sendAnalyticEvent } = useAnalyticEvents('hero_pdp');

  useHideGlobalScrollbar(showImageSwiper, 'y');

  const thumbNbToShowButtons: number = isHeroPdpStyles ? 5 : 6;

  const getSEOImgPath = (path: string): string => {
    return ImagesService.improveSEOProductPathImg(path, productPath);
  };

  const scrollableZone = useRef(null);
  const el = scrollableZone.current;

  const scrollToNext = () => {
    const el = scrollableZone.current;
    const top = el.scrollTop + el.offsetHeight;
    el.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollToPrev = () => {
    const top = el.scrollTop - el.offsetHeight;
    el.scrollTo({ top, behavior: 'smooth' });
  };

  const onThumbScroll = () => {
    // This function will be triggered a lot, do not do complex computing inside, or use debounce.
    const { scrollTop, scrollHeight, offsetHeight } = el;
    setPrevButtonDisabled(scrollTop === 0);
    setNextButtonDisabled(scrollHeight - (scrollTop + offsetHeight) === 0);
  };

  const open = (imageSources: Array<GalleryPicture>, indexParam) => {
    setActivePicIndex(indexParam);
    setShowImageSwiper(true);
    setSwiperImages(imageSources);
  };

  const toGalleryPicture = (p: Picture): GalleryPicture => ({
    // Apparently 'produit' in the url is not a typo
    src: ImagesService.buildUrl(getSEOImgPath(`/produit/${p.path}`), { w: p.width || 1024 }, true),
    w: p.width || 1400, // TODO How do we define the height of the image (know it can be zoomed 2x) ?
    h: p.height || 1400,
  });

  const getPicIndex = (imgSrc: string, imageSources: Array<GalleryPicture>): number => {
    const imgName = imgSrc.split('/').slice(-1)[0];
    const nameArr = imageSources.map((e) => e.src.split('/').slice(-1)[0]);
    return nameArr.findIndex((n) => imgName.includes(n));
  };

  // used in mobile and pc
  const openDialog = (event?: React.MouseEvent<HTMLElement>, argument?: Picture, index?: number) => {
    const imageSources = pictures.map(toGalleryPicture);
    let picIndex = index;
    if (event) {
      const initiator = (event.target as HTMLElement).parentElement.querySelector('img');
      picIndex = getPicIndex(initiator.src, imageSources);
    }
    sendAnalyticEvent('hero_pdp_photo_zoom', {
      value: picIndex + 1,
      label: '',
    });
    open(imageSources, picIndex);
  };

  // used in mobile
  const openImg = (argument: Picture, index: number) => {
    openDialog(null, argument, index);
  };

  const closePhotoSwiper = () => {
    setShowImageSwiper(false);
  };

  const handleHoverImage = (picture: Picture, index) => {
    setImage(picture);
    if (index + 1 >= 2 && !imageViewEventList.includes(index)) {
      sendAnalyticEvent('hero_pdp_photo_view_vslider', {
        category: 'product',
        action: 'photo_view',
        label: 'vertical_slider',
        value: index + 1,
      });
      setImageViewEventList([...imageViewEventList, index]);
    }
  };

  useEffect(() => {
    setImage(pictures?.[0]);
  }, [pictures]);

  return (
    <>
      <div className={clsx(styles.pdtImg, contextCssClass, { [styles['pdtImg--semPDPIteration']]: isSemPDP })}>
        <div className={styles.pdtImg__main}>
          <div
            className={clsx(styles.slide, styles.custom, {
              [styles['slide--heroPdp']]: isHeroPdpStyles,
            })}
            role="presentation"
            onClick={openDialog}
          >
            <ProductImage
              height={500}
              width={500}
              src={getSEOImgPath(image.path)}
              alt={image.alt}
              type="main"
              priority={isWiderThanMd}
              disableLazyLoad
              fetchpriority="high"
            />
          </div>
        </div>
        <div
          className={clsx(styles.pdtImg__nav, {
            [styles['pdtImg__nav--heroPdp']]: isHeroPdpStyles,
          })}
        >
          {pictures?.length > thumbNbToShowButtons && (
            <button
              className={clsx(styles.thumbControl, styles.thumbControl__prev, 'leftRs')}
              type="submit"
              disabled={prevButtonDisabled}
              onClick={scrollToPrev}
              tabIndex={-1}
              aria-label="Up"
            >
              <Icon name="arrow-head-up" />
            </button>
          )}
          <div
            className={styles.pdtImg__nav__thumbs}
            ref={scrollableZone}
            onScroll={onThumbScroll}
          >
            {pictures.map((picture: Picture, index) => (
              <div
                role="button"
                tabIndex={0}
                key={picture.path}
                className={styles.pdtImg__nav__thumb}
                onMouseEnter={() => handleHoverImage(picture, index)}
              >
                <div>
                  <ProductImage
                    sizesOverwrite={[128]}
                    src={getSEOImgPath(picture.path)}
                    width={60}
                    height={60}
                    quality={70}
                    alt={picture?.alt || defaultAlt}
                    type="thumbnail"
                    fetchpriority="low"
                  />
                </div>
              </div>
            ))}
          </div>
          {pictures?.length > thumbNbToShowButtons && (
            <button
              type="submit"
              className={clsx(styles.thumbControl, styles.thumbControl__next, 'rightRs')}
              disabled={nextButtonDisabled}
              onClick={scrollToNext}
              tabIndex={-1}
              aria-label="Down"
            >
              <Icon name="arrow-head-down" />
            </button>
          )}
        </div>
      </div>
      <div
        className={clsx(styles.pdtImg__main, styles['pdtImg__main--control'], {
          [styles['pdtImg__main--heroPdp']]: isHeroPdpStyles,
        })}
      >
        <Slider
          clickIMG={openImg}
          pictures={pictures}
          productPath={productPath}
        />
      </div>
      {showImageSwiper && (
        <PhotoSwipe
          images={swiperImages}
          closeHandler={closePhotoSwiper}
          defaultActivePicIndex={activePicIndex}
        />
      )}
    </>
  );
};

export default ProductGallery;
